import React from 'react'
import { Button, DialogOverlay, DialogOverlayFooter, DialogOverlayActions, DangerButton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

/**
 * UnsavedChangesOverlay component renders a dialog overlay to prompt the user to save or discard unsaved changes.
 *
 * @param {Object} params - The component params.
 * @param {Object} params.overlay - The overlay properties to be spread onto the DialogOverlay component from the useOverlay hook.
 * @param {Function} params.onDiscard - The function to call when the discard button is clicked.
 * @param {Function} params.onSave - The function to call when the save button is clicked.
 * @param {string} params.formId - The ID of the form to be submitted when the save button is clicked.
 * @param {string} [params.headingText] - An optional heading text for the dialog overlay.
 * @param {JSX.Element} [params.contentText] - An optional content text for the dialog overlay.
 * @param {string} [params.saveButtonText] - An optional text for the save button.
 * @param {string} [params.discardButtonText] - An optional text for the discard button.
 * @param {boolean} [params.disableSave=false] - An optional flag to disable the save button.
 * @param {boolean} [params.alertMode=false] - Aler mode will just show the dialog with the option to continue editing and not actually saving.
 * @returns {JSX.Element} The rendered component.
 */
export default function UnsavedChangesOverlay({
  overlay,
  onDiscard,
  onSave,
  formId,
  headingText: passedHeadingText,
  contentText: passedContentText,
  saveButtonText: passedSaveButtonText,
  discardButtonText: passedDiscardButtonText,
  disableSave = false,
  alertMode = false,
}) {
  const [t] = useTranslation()
  const headingText = passedHeadingText || t('edit-index.save-changes-overlay.header', 'Unsaved Changes')
  const contentText =
    passedContentText ||
    t('edit-index.save-changes-overlay.content', 'You have unsaved changes that will be lost if you exit.')
  const saveButtonText =
    passedSaveButtonText || alertMode
      ? t('edit-index.save-changes-overlay.continue.button', 'Continue Editing')
      : t('edit-index.save-changes-overlay.save.button', 'save')
  const discardButtonText =
    passedDiscardButtonText || t('edit-index.save-changes-overlay.discard.button', 'Discard Changes')

  const basicDiscard = () => overlay?.close()
  const handleDiscard = onDiscard || basicDiscard

  return (
    <DialogOverlay {...overlay} title={headingText} nonClosable>
      {contentText}
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <DangerButton data-testid="save-changes-overlay-cancel-button" onClick={handleDiscard}>
            {discardButtonText}
          </DangerButton>
          <Button
            data-testid="save-changes-overlay-save-button"
            onClick={alertMode ? basicDiscard : onSave}
            emphasis="high"
            form={formId}
            disabled={disableSave}
          >
            {saveButtonText}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
